import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { RefundApi } from 'api';
import type { Refund, OrderRefund } from 'api/methods/RefundApi';

/**
 * [Refund] Query key 생성
 * --
 */
export const RefundQueryKeys = createQueryKeys('review', {
  getRefund: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getRefunds: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [Refund] 환불 오더 수정
 * --
 */
export const useCreateRefundOrder = () => {
  const { mutateAsync: createRefundOrder, ...result } = useMutation({
    mutationFn: (variables: OrderRefund) =>
      RefundApi.createRefundOrder(variables),
  });

  return { createRefundOrder, ...result };
};

/**
 * [Refund] 환불 전체 조회
 * --
 */
export const useGetRefunds = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: RefundQueryKeys.getRefunds(params).queryKey,
    queryFn: () => RefundApi.getRefunds(params),
    ...options,
  });
  return data;
};

/**
 * [Refund] 환불 상세 조회
 * --
 */
export const useGetRefund = (
  idx: number,
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: RefundQueryKeys.getRefund(idx, params).queryKey,
    queryFn: () => RefundApi.getRefund(idx, params),
    ...options,
  });

  return data;
};

/**
 * [Refund] 환불 생성
 * --
 */
export const useCreateRefund = (body: Refund) => {
  const { mutateAsync: createRefund, ...result } = useMutation({
    mutationFn: (variables: Refund) => RefundApi.createRefund(variables),
  });

  return { createRefund, ...result };
};

/**
 * [Refund] 환불 수정
 * --
 */
export const useUpdateRefund = () => {
  const { mutateAsync: updateRefund, ...result } = useMutation({
    mutationFn: (variables: Refund) => RefundApi.updateRefund(variables),
  });

  return { updateRefund, ...result };
};

/**
 * [Refund] 환불 삭제
 * --
 */
export const useDeleteRefund = () => {
  const { mutateAsync: deleteRefund, ...result } = useMutation({
    mutationFn: (variables: number) => RefundApi.deleteRefund(variables),
  });

  return { deleteRefund, ...result };
};

/**
 * [Refund] 쇼핑몰 배송 환불
 * --
 */
export const useCreateRefundDelivery = () => {
  const { mutateAsync: createRefundDelivery, ...result } = useMutation({
    mutationFn: (variables: Refund) =>
      RefundApi.createRefundDelivery(variables),
  });

  return { createRefundDelivery, ...result };
};

/**
 * [Refund] 쇼핑몰 정기배송 환불
 * --
 */
export const useCreateRefundSubscription = () => {
  const { mutateAsync: createRefundSubscription, ...result } = useMutation({
    mutationFn: (variables: Refund) =>
      RefundApi.createRefundSubscription(variables),
  });

  return { createRefundSubscription, ...result };
};

/**
 * [Refund] 쇼핑몰 렌탈 환불
 * --
 */
export const useCreateRefundRental = () => {
  const { mutateAsync: createRefundRental, ...result } = useMutation({
    mutationFn: (variables: Refund) => RefundApi.createRefundRental(variables),
  });

  return { createRefundRental, ...result };
};

/**
 * [Refund] 쇼핑몰 배송 환불
 * --
 */
export const useCreateRefundDeliveryManual = () => {
  const { mutateAsync: createRefundDeliveryManual, ...result } = useMutation({
    mutationFn: (variables: Refund) =>
      RefundApi.createRefundDeliveryManual(variables),
  });

  return { createRefundDeliveryManual, ...result };
};

/**
 * [Refund] 쇼핑몰 정기배송 환불
 * --
 */
export const useCreateRefundSubscriptionManual = () => {
  const { mutateAsync: createRefundSubscriptionManual, ...result } =
    useMutation({
      mutationFn: (variables: Refund) =>
        RefundApi.createRefundSubscriptionManual(variables),
    });

  return { createRefundSubscriptionManual, ...result };
};

/**
 * [Refund] 쇼핑몰 렌탈 환불
 * --
 */
export const useCreateRefundRentalManual = () => {
  const { mutateAsync: createRefundRentalManual, ...result } = useMutation({
    mutationFn: (variables: Refund) =>
      RefundApi.createRefundRentalManual(variables),
  });

  return { createRefundRentalManual, ...result };
};
